import {useCallback, useState} from 'react'
import {useUpdateBypass} from './useUpdateBypass'
import Toast from 'components/Basic/Toast'
import {useTranslation} from 'react-i18next'

interface UseBypassMutationsProps<T = unknown> {
  resetEditMode: () => void
  refetchSelectedPolicyBypassData: () => void
  onSelectBypass: (value?: T) => void
}

interface UseBypassMutationsReturn {
  mutateSingle: ReturnType<typeof useUpdateBypass>['mutate']
  mutateBulk: ReturnType<typeof useUpdateBypass>['mutate']
  mutateGeneral: ReturnType<typeof useUpdateBypass>['mutate']
  isAddingSingle: boolean
  isAddingBulk: boolean
  isMutatingGeneral: boolean
  isAnyMutationLoading: boolean
  pendingSuccessCount: number
  setPendingSuccessCount: (count: number) => void
  setIsSubmittingAddDomainData: (isSubmitting: boolean) => void
  setShowResetToBasePrompt: (show: boolean) => void
  selectedRowIndex: number
  setSelectedRowIndex: (index: number) => void
  isSubmittingAddDomainData: boolean
  showResetToBasePrompt: boolean
}

/**
 * Custom hook to manage bypass mutations with consistent patterns across URL and Application bypass components
 * Provides three mutation instances: single item add, bulk add, and general mutations (edit, traffic change, reset)
 */
export const useBypassMutations = <T = unknown,>({
  resetEditMode,
  refetchSelectedPolicyBypassData,
  onSelectBypass,
}: UseBypassMutationsProps<T>): UseBypassMutationsReturn => {
  const {t} = useTranslation(['policies'])
  const [pendingSuccessCount, setPendingSuccessCount] = useState<number>(0)
  const [isSubmittingAddDomainData, setIsSubmittingAddDomainData] =
    useState<boolean>(false)
  const [showResetToBasePrompt, setShowResetToBasePrompt] =
    useState<boolean>(false)

  const handleCommonCleanup = useCallback(() => {
    // Reset forms and edit mode first before stopping loading state
    // This prevents flickering of submit/cancel buttons
    resetEditMode()
    refetchSelectedPolicyBypassData()
    setShowResetToBasePrompt(false)
    onSelectBypass(undefined)

    // Delay stopping the loading state slightly to ensure form reset completes
    // This prevents the submit/cancel buttons from briefly appearing before the input is cleared
    setTimeout(() => {
      setIsSubmittingAddDomainData(false)
    }, 0)
  }, [resetEditMode, refetchSelectedPolicyBypassData, onSelectBypass])

  const handleErrorCleanup = useCallback(() => {
    setIsSubmittingAddDomainData(false)
    setPendingSuccessCount(0)
  }, [])

  // Mutation for single item add with success message
  const {mutate: mutateSingle, isLoading: isAddingSingle} = useUpdateBypass({
    onSuccess: () => {
      handleCommonCleanup()
      // Success message is handled by the calling component
    },
    onError: () => {
      handleErrorCleanup()
      Toast({
        message: t('BYPASS_UPDATE_FAILED'),
        type: 'error',
        hideIcon: true,
      })
    },
  })

  // Mutation for bulk add with count-based success message
  const {mutate: mutateBulk, isLoading: isAddingBulk} = useUpdateBypass({
    onSuccess: () => {
      handleCommonCleanup()
      // Bulk success message with count is handled by the calling component
      // to allow it to pass the correct count
    },
    onError: () => {
      handleErrorCleanup()
      Toast({
        message: t('BYPASS_UPDATE_FAILED'),
        type: 'error',
        hideIcon: true,
      })
    },
  })

  // Mutation for general operations (edit, traffic change, reset)
  const {mutate: mutateGeneral, isLoading: isMutatingGeneral} = useUpdateBypass(
    {
      onSuccess: () => {
        handleCommonCleanup()
      },
      onError: () => {
        handleErrorCleanup()
        Toast({
          message: t('BYPASS_UPDATE_FAILED'),
          type: 'error',
          hideIcon: true,
        })
      },
    },
  )

  const isAnyMutationLoading =
    isMutatingGeneral || isAddingSingle || isAddingBulk

  // Helper to manage selected row index
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1)

  return {
    mutateSingle,
    mutateBulk,
    mutateGeneral,
    isAddingSingle,
    isAddingBulk,
    isMutatingGeneral,
    isAnyMutationLoading,
    pendingSuccessCount,
    setPendingSuccessCount,
    setIsSubmittingAddDomainData,
    setShowResetToBasePrompt,
    selectedRowIndex,
    setSelectedRowIndex,
    isSubmittingAddDomainData,
    showResetToBasePrompt,
  }
}
